import { sortBy } from 'lodash-es';

export enum ContactType {
  Phone = 'ph',
  Email = 'em',
}

export enum PhoneType {
  Mobile = 'm',
  NA = 'na',
  LandLine = 'll',
  VOIP = 'v',
}

export enum ConsentApplicationType {
  Marketing = 'm',
  PatientBusiness = 'p',
}

export enum ConsentStatus {
  Granted = 'g',
  Revoked = 'r',
}

// API Models

export interface ApiPatientContactConsentItem {
  appliesTo: ConsentApplicationType;
  consentStatus: ConsentStatus;
}

export interface ApiPatientContactResponseItem {
  active: boolean;
  preferenceOrder: number;
  contactValue: string;
  contactType: string;
  contactSubType: string;
  consents: ApiPatientContactConsentItem[];
}

export interface ApiUpdatePatientNameRequest {
  patientId: number;
  firstName?: string;
  lastName: string;
}

export interface ApiUpdatePatientNameResponse extends ApiUpdatePatientNameRequest {}

export interface ApiUpdatePatientContactsAndConsentRequest {
  patientId: number;
  primaryEmail: string;
  primaryPhone: string;
  primaryPhoneSubType: PhoneType;
  secondaryPhone?: string;
  secondaryPhoneSubType?: PhoneType;
  emailConsentMarketing: boolean;
  emailConsentPatientBusiness: true;
  phoneConsentMarketing: boolean;
  phoneConsentPatientBusiness: true;
}

// FE Models

export interface ContactPhoneNumber {
  phoneNumber: string;
  isMobile: boolean;
  hasMarketingConsent: boolean;
}

export interface PatientContacts {
  primaryEmail?: string;
  primaryPhone?: ContactPhoneNumber;
  secondaryPhone?: ContactPhoneNumber;
}

// Form Models
export interface PatientDetailsFormValues {
  firstName: string;
  lastName: string;
  primaryEmail: string | null;
  primaryPhoneNumber: string | null;
  isPrimaryPhoneMobile: boolean;
  secondaryPhoneNumber: string | null;
  isSecondaryPhoneMobile: boolean;
  consentedToMarketing: boolean;
  refusedGenericMedications: boolean;
}

export const mapPhoneContact = (phoneContact: ApiPatientContactResponseItem): ContactPhoneNumber => {
  return {
    phoneNumber: phoneContact.contactValue,
    isMobile: phoneContact.contactSubType === PhoneType.Mobile,
    hasMarketingConsent: !!phoneContact.consents.find(
      (c) => c.appliesTo === ConsentApplicationType.Marketing && c.consentStatus === ConsentStatus.Granted
    ),
  };
};

export const mapPatientContacts = (contacts: ApiPatientContactResponseItem[]): PatientContacts => {
  const emailContacts = contacts.filter((c) => c.contactType === ContactType.Email && c.active === true);
  const primaryEmail = emailContacts.length > 0 ? sortBy(emailContacts, 'preferenceOrder')[0].contactValue : undefined;
  const phoneContacts = contacts.filter((c) => c.contactType === ContactType.Phone && c.active === true);
  const primaryPhoneContact = phoneContacts.length > 0 ? sortBy(phoneContacts, 'preferenceOrder')[0] : undefined;
  const secondaryPhoneContact = phoneContacts.length > 1 ? sortBy(phoneContacts, 'preferenceOrder')[1] : undefined;

  let primaryPhone = undefined;
  let secondaryPhone = undefined;

  if (primaryPhoneContact) primaryPhone = mapPhoneContact(primaryPhoneContact);
  if (secondaryPhoneContact) secondaryPhone = mapPhoneContact(secondaryPhoneContact);

  return {
    primaryEmail,
    primaryPhone,
    secondaryPhone,
  };
};

export const buildUpdatePatientNameRequest = (
  patientId: number,
  formValues: PatientDetailsFormValues
): ApiUpdatePatientNameRequest => {
  const { firstName, lastName } = formValues;
  return {
    patientId,
    firstName,
    lastName,
  };
};

export const buildUpdatePatientContactsAndConsentRequest = (
  patientId: number,
  formValues: PatientDetailsFormValues,
  patientContacts: PatientContacts | null
): ApiUpdatePatientContactsAndConsentRequest => {
  return {
    patientId,
    primaryEmail: patientContacts?.primaryEmail || '',
    primaryPhone: formValues.primaryPhoneNumber || '',
    primaryPhoneSubType: formValues.isPrimaryPhoneMobile ? PhoneType.Mobile : PhoneType.LandLine,
    secondaryPhone: formValues.secondaryPhoneNumber || undefined,
    secondaryPhoneSubType: formValues.isSecondaryPhoneMobile ? PhoneType.Mobile : PhoneType.LandLine,
    emailConsentMarketing: formValues.consentedToMarketing,
    emailConsentPatientBusiness: true,
    phoneConsentMarketing: patientContacts?.primaryPhone?.hasMarketingConsent || false,
    phoneConsentPatientBusiness: true,
  };
};
